import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import LegalAcceptance from "../../components/LegalAcceptance";
import { startGoogleLogin } from "../../services/googleOAuth";
import type { AuthProviders } from "../../services/sessionAuth";
import AppButton from "../../components/form/AppButton";
import AppTextField from "../../components/form/AppTextField";
import { AppFormField, AppFormGrid, AuthPageShell } from "../../components/layout";
import sessionAuthApi, { AuthApiError } from "../../services/sessionAuth";
import { useDeploymentMode } from "../../hooks/useDeploymentMode";
import { isEmailDomainAllowed } from "../../services/deploymentModeService";
import { authEntryButtonSx } from "../login/authEntryButtonSx";

type RegisterForm = {
  email: string;
  username: string;
  password: string;
  workspaceName: string;
  firstName: string;
  lastName: string;
  captchaAnswer: string;
};

type FieldErrors = Partial<Record<keyof RegisterForm, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirrors the constraints on the backend RegisterRequest.
function validate(
  form: RegisterForm,
  t: TFunction,
  invited: boolean,
  onPremDomains: string[] | null
): FieldErrors {
  const errors: FieldErrors = {};
  const workspaceName = form.workspaceName.trim();
  const username = form.username.trim();
  if (!invited && !onPremDomains && (workspaceName.length < 2 || workspaceName.length > 80)) {
    errors.workspaceName = t("auth.register.validation.workspaceNameLength");
  }
  if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = t("auth.register.validation.emailInvalid");
  } else if (onPremDomains && !isEmailDomainAllowed(form.email, onPremDomains)) {
    errors.email = t("auth.onprem.domainNotAllowed");
  }
  if (username.length < 3 || username.length > 64) {
    errors.username = t("auth.register.validation.usernameLength");
  }
  if (form.password.length < 8 || form.password.length > 128) {
    errors.password = t("auth.register.validation.passwordLength");
  }
  if (!form.captchaAnswer.trim()) {
    errors.captchaAnswer = t("auth.register.captcha.required");
  }
  return errors;
}

function describeError(err: unknown, t: TFunction): { field?: keyof RegisterForm; message: string } {
  const apiError = err instanceof AuthApiError ? err : undefined;
  switch (apiError?.code) {
    case "AUTH_EMAIL_DOMAIN_NOT_ALLOWED":
      return { field: "email", message: t("auth.onprem.domainNotAllowed") };
    case "AUTH_USERNAME_TAKEN":
      return { field: "username", message: t("auth.register.errors.usernameTaken") };
    case "AUTH_EMAIL_TAKEN":
      return { field: "email", message: t("auth.register.errors.emailTaken") };
    case "AUTH_USERNAME_INVALID":
      return { field: "username", message: t("auth.register.errors.usernameInvalid") };
    case "AUTH_EMAIL_INVALID":
      return { field: "email", message: t("auth.register.errors.emailInvalid") };
    case "AUTH_PASSWORD_POLICY":
      return { field: "password", message: t("auth.register.errors.passwordPolicy", { detail: apiError.message }) };
    case "CAPTCHA_INVALID":
    case "FREE_PILOT_CAPTCHA_INVALID":
      return { field: "captchaAnswer", message: t("auth.register.errors.captchaInvalid") };
    case "AUTH_INVITE_INVALID":
      return { message: t("auth.invite.invalid") };
    case "AUTH_INVITE_EMAIL_MISMATCH":
      return { field: "email", message: t("auth.invite.emailMismatch") };
    case "AUTH_RATE_LIMITED":
      return { message: t("auth.register.errors.rateLimited") };
    case "NETWORK_ERROR":
      return { message: t("auth.register.errors.network") };
  }
  if (apiError?.status === 400) {
    return { message: t("auth.register.errors.invalidInput") };
  }
  if (apiError && apiError.status >= 500) {
    return { message: t("auth.register.errors.serviceUnavailable") };
  }
  return { message: t("auth.register.errors.generic") };
}

export default function RegisterUI() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const deployment = useDeploymentMode();
  const onPrem = deployment.onPrem;
  // On-prem: no workspace of your own and no invitations; membership comes from the e-mail domain.
  const invite = onPrem ? null : params.get("invite");
  const [form, setForm] = useState<RegisterForm>({
    email: "",
    username: "",
    password: "",
    workspaceName: "",
    firstName: "",
    lastName: "",
    captchaAnswer: "",
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [legalError, setLegalError] = useState(false);
  const [inviteWorkspace, setInviteWorkspace] = useState<string | null>(null);
  const [providers, setProviders] = useState<AuthProviders | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setForm((prev) => ({ ...prev, captchaAnswer: "" }));
    try {
      const data = await sessionAuthApi.getCaptcha();
      setCaptchaToken(data.captchaToken || "");
      setCaptchaImage(data.captchaImage || "");
    } catch {
      setCaptchaToken("");
      setCaptchaImage("");
      setError(t("auth.register.captcha.loadFailed"));
    } finally {
      setCaptchaLoading(false);
    }
  }, [t]);

  useEffect(() => {
    sessionAuthApi.getAuthProviders().then(setProviders).catch(() => setProviders(null));
  }, []);

  useEffect(() => {
    void loadCaptcha();
  }, [loadCaptcha]);

  const requireLegal = (): boolean => {
    if (acceptedLegal) {
      setLegalError(false);
      return true;
    }
    setLegalError(true);
    setError(t("auth.register.legal.required"));
    return false;
  };

  const onGoogle = () => {
    if (!requireLegal()) return;
    // Workspace (and any other extras) are collected in our UI — never Keycloak registration.
    if (invite) {
      if (!providers) return;
      startGoogleLogin(providers, { invite }).catch(() =>
        setError(t("auth.registerGoogle.unavailable"))
      );
      return;
    }
    navigate("/register/google");
  };

  useEffect(() => {
    if (!invite) return;
    sessionAuthApi
      .previewInvite(invite)
      .then((preview) => {
        if (preview.email) {
          setForm((prev) => ({ ...prev, email: preview.email || "" }));
        }
        setInviteWorkspace(preview.workspaceName || null);
      })
      .catch(() => setError(t("auth.invite.invalid")));
  }, [invite, t]);

  const update = (field: keyof RegisterForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const errors = validate(form, t, Boolean(invite), onPrem ? deployment.allowedEmailDomains : null);
    setFieldErrors(errors);
    if (!requireLegal() || Object.keys(errors).length > 0) {
      return;
    }
    if (!captchaToken) {
      setError(t("auth.register.captcha.loadFailed"));
      await loadCaptcha();
      return;
    }
    setLoading(true);
    try {
      const res = await sessionAuthApi.register({
        email: form.email,
        username: form.username,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        captchaToken,
        captchaAnswer: form.captchaAnswer.trim(),
        ...(invite
          ? { inviteToken: invite }
          : onPrem
            ? {}
            : { workspaceName: form.workspaceName }),
      });
      if (invite) {
        setInfo(t("auth.invite.registerSuccess"));
        setTimeout(() => navigate("/login"), 800);
        return;
      }
      const message =
        res.mailSent === false ? t("auth.register.mailNotSent") : t("auth.register.success");
      setInfo(message + (res.verificationToken ? ` Dev token: ${res.verificationToken}` : ""));
      setTimeout(() => navigate("/verify-email"), 1200);
    } catch (err) {
      const { field, message } = describeError(err, t);
      if (field) {
        setFieldErrors({ [field]: message });
      } else {
        setError(message);
      }
      await loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const fieldProps = (field: keyof RegisterForm) => ({
    value: form[field],
    error: Boolean(fieldErrors[field]),
    helperText: fieldErrors[field],
    onChange: (e: ChangeEvent<HTMLInputElement>) => update(field, e.target.value),
  });

  return (
    <AuthPageShell
      maxWidth={480}
      title={invite ? t("auth.invite.registerTitle") : onPrem ? t("auth.onprem.registerTitle") : t("auth.register.title")}
      subtitle={
        invite
          ? t("auth.invite.registerSubtitle", { workspace: inviteWorkspace || t("auth.invite.workspaceFallback") })
          : onPrem
            ? t("auth.onprem.registerSubtitle")
            : t("auth.register.subtitle")
      }
      footer={
        <Link to={invite ? `/login?invite=${encodeURIComponent(invite)}` : "/login"}>
          {t("auth.register.haveAccount")}
        </Link>
      }
    >
      <Box component="form" noValidate onSubmit={onSubmit}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}
          {onPrem && deployment.allowedEmailDomains.length > 0 && (
            <Alert severity="info">
              {t("auth.onprem.domainHint", { domains: deployment.allowedEmailDomains.join(", ") })}
            </Alert>
          )}
          {!invite && !onPrem && (
            <AppTextField label={t("auth.register.workspaceName")} required {...fieldProps("workspaceName")} />
          )}
          <AppTextField
            label={t("auth.register.email")}
            type="email"
            required
            autoComplete="email"
            disabled={Boolean(invite)}
            {...fieldProps("email")}
          />
          <AppTextField
            label={t("auth.register.username")}
            required
            autoComplete="username"
            {...fieldProps("username")}
          />
          <AppTextField
            label={t("auth.register.password")}
            type="password"
            required
            autoComplete="new-password"
            {...fieldProps("password")}
          />
          <AppFormGrid spacing="compact">
            <AppFormField size={{ xs: 12, sm: 6 }}>
              <AppTextField label={t("auth.register.firstName")} {...fieldProps("firstName")} />
            </AppFormField>
            <AppFormField size={{ xs: 12, sm: 6 }}>
              <AppTextField label={t("auth.register.lastName")} {...fieldProps("lastName")} />
            </AppFormField>
          </AppFormGrid>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle2">{t("auth.register.captcha.label")}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {captchaLoading || !captchaImage ? (
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220, minHeight: 72, display: "flex", alignItems: "center" }}>
                    {t("auth.register.captcha.loading")}
                  </Typography>
                ) : (
                  <Box
                    component="img"
                    src={captchaImage}
                    alt={t("auth.register.captcha.label")}
                    sx={{
                      width: 220,
                      height: 72,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      userSelect: "none",
                    }}
                  />
                )}
                <AppButton type="button" variant="outlined" size="small" onClick={() => void loadCaptcha()} disabled={captchaLoading}>
                  {t("auth.register.captcha.refresh")}
                </AppButton>
              </Stack>
              <AppTextField
                label={t("auth.register.captcha.answer")}
                required
                autoComplete="off"
                inputProps={{ autoCapitalize: "characters", spellCheck: false }}
                {...fieldProps("captchaAnswer")}
              />
            </Stack>
          </Box>
          <LegalAcceptance
            checked={acceptedLegal}
            error={legalError}
            helperText={legalError ? t("auth.register.legal.required") : null}
            onChange={(value) => {
              setAcceptedLegal(value);
              if (value) {
                setLegalError(false);
                setError(null);
              }
            }}
          />
          <AppButton type="submit" fullWidth variant="contained" sx={{ mt: 1.5, ...authEntryButtonSx }} loading={loading}>
            {t("auth.register.submit")}
          </AppButton>
        </Stack>
      </Box>
      {providers?.googleEnabled && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Divider>or</Divider>
          <GoogleSignInButton onClick={onGoogle} label={t("auth.registerGoogle.button")} sx={authEntryButtonSx} />
        </Stack>
      )}
    </AuthPageShell>
  );
}
